import React from 'react';
import { Button, Radio, Space } from 'antd';
import { useIntl } from 'umi';

import Drawer from '@/components/Drawer';
import { F } from '@/utils/intl';
import { RecurrenceScope } from '@/typings/models/Recurrence';

export type RecurrenceScopeAction = 'edit' | 'delete';

interface RecurrenceScopeDrawerProps {
  open: boolean;
  action: RecurrenceScopeAction;
  /** When false (e.g. the recurrence rule itself changed) the "This event" option is hidden. */
  allowThisEvent?: boolean;
  defaultScope?: RecurrenceScope;
  onConfirm: (scope: RecurrenceScope) => void;
  onCancel: () => void;
}

/**
 * The Google-style prompt shown when editing or deleting an event that belongs to
 * a recurring series. Lets the user scope the change to this event, this event and
 * the following ones, or the whole series. Opens as a drawer stacked on top of
 * whatever surface triggered it (see `@/components/Drawer`, which stacks nested
 * instances by z-index) rather than a modal, matching {@link CustomRecurrenceDrawer}.
 */
const RecurrenceScopeDrawer: React.FC<RecurrenceScopeDrawerProps> = ({
  open,
  action,
  allowThisEvent = true,
  defaultScope,
  onConfirm,
  onCancel,
}) => {
  const intl = useIntl();
  const initialScope = allowThisEvent
    ? defaultScope || RecurrenceScope.ONLY_THIS
    : RecurrenceScope.THIS_AND_FOLLOWING;
  const [scope, setScope] = React.useState<RecurrenceScope>(initialScope);

  React.useEffect(() => {
    if (open) setScope(initialScope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, allowThisEvent]);

  const title =
    action === 'delete'
      ? intl.formatMessage({ id: 'pages.calendar.recurrence.deleteTitle', defaultMessage: 'Delete recurring event' })
      : intl.formatMessage({ id: 'pages.calendar.recurrence.editTitle', defaultMessage: 'Edit recurring event' });

  const okText =
    action === 'delete'
      ? intl.formatMessage({ id: 'common.actions.remove', defaultMessage: 'Delete' })
      : intl.formatMessage({ id: 'common.actions.ok', defaultMessage: 'OK' });

  return (
    <Drawer
      data-testid="recurrence-scope-drawer"
      open={open}
      width={480}
      title={title}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button size={'large'} onClick={onCancel} data-testid="recurrence-scope-cancel">
              {intl.formatMessage({ id: 'common.actions.cancel', defaultMessage: 'Cancel' })}
            </Button>
            <Button
              size={'large'}
              type={'primary'}
              danger={action === 'delete'}
              onClick={() => onConfirm(scope)}
              data-testid="recurrence-scope-confirm"
            >
              {okText}
            </Button>
          </Space>
        </div>
      }
    >
      <Radio.Group value={scope} onChange={(e) => setScope(e.target.value)}>
        <Space direction="vertical">
          {allowThisEvent && (
            <Radio value={RecurrenceScope.ONLY_THIS} data-testid="recurrence-scope-this">
              <F id="pages.calendar.recurrence.scope.this" defaultMessage="This event" />
            </Radio>
          )}
          <Radio value={RecurrenceScope.THIS_AND_FOLLOWING} data-testid="recurrence-scope-following">
            <F id="pages.calendar.recurrence.scope.following" defaultMessage="This and following events" />
          </Radio>
          <Radio value={RecurrenceScope.ALL} data-testid="recurrence-scope-all">
            <F id="pages.calendar.recurrence.scope.all" defaultMessage="All events" />
          </Radio>
        </Space>
      </Radio.Group>
    </Drawer>
  );
};

export default RecurrenceScopeDrawer;
